import {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {animated, useSpring} from '@react-spring/web';
import { Drawer } from 'antd';
import {
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  MenuOutlined,
  MobileFilled,
  MobileOutlined,
} from '@ant-design/icons';

import {hooks} from '../hooks';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {RootState} from '../store';
import {components} from '../components';
import {actions} from '../store/actions';

type Props = {
  title?: string;
  line?: boolean;
  burger?: boolean;
  goBack?: boolean;
  basket?: boolean;
  search?: boolean;  
};

export const Header: React.FC<Props> = ({
  line,
  title,
  burger,
  goBack,
  basket,
  search,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [modal, setModal] = useState(false);

  const backdropAnimation = useSpring({
    opacity: modal ? 1 : 0,
    display: modal ? 'block' : 'none',
  });

  const dispatch = hooks.useAppDispatch();

  useEffect(() => {
    if (modal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
    // document.body.style.overflow = 'hidden';
  }, [modal]);

  const cart = hooks.useAppSelector((state: RootState) => state.cartSlice.list);
  const payAmount = hooks.useAppSelector(
    (state: RootState) => state.cartSlice.payAmount,
  );

  const handleOnClick = () => {
    if (cart.length > 0) {
      dispatch(actions.setScreen('Order'));
      navigate('/home');
      return;
    }

    return alert('سبد خرید شما خالی است');
  };

  const renderGoBack = (): JSX.Element | null => {
    if (goBack) {
      return (
        <button
          style={{
            position: 'absolute',
            right: 0,
            padding: 20,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'none',
            rotate: '180deg'
          }}
          onClick={() => navigate(-1)}
        >
          <svg.GoBackSvg />
        </button>
      );
    }

    return null;
  };

  const renderBurger = (): JSX.Element | null => {
    if (burger) {
      return (
        <button
        style={{
            position: 'absolute',
            right: 0,
            padding: 20,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'none',
            rotate: '180deg'
          }}
          onClick={() => setModal(true)}
          className="absolute right-0 p-5 bg-transparent border-none cursor-pointer"
        >
          <MenuOutlined style={{ fontSize: '20px', color: theme.colors.mainColor }} />
        </button>
      );
    }

    return null;
  };

  const renderTitle = (): JSX.Element => {
    return (
      <h4
        style={{
          width: 'calc(100% - 100px)',
          textAlign: 'center',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          ...theme.fonts.Mulish_600SemiBold,
          fontSize: 18,
          color: theme.colors.mainColor,
        }}
      >
        {title}
      </h4>
    );
  };

  const renderBasket = (): JSX.Element | null => {
    if (basket) {
      return (
        <button
          onClick={handleOnClick}
          style={{
            position: 'absolute',
            left: 0,
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 20,
            paddingRight: 20,
            borderRadius: 12,
          }}
        >
          <svg.BasketSvg />
          <div
            style={{
              paddingLeft: 6,
              paddingRight: 6,
              borderRadius: 10,
              left: 35,
              bottom: 10,
              position: 'absolute',
              display: 'flex',
              paddingTop: 2,
              paddingBottom: 2,
              fontSize: 10,
              color: theme.colors.white,
              ...theme.fonts.Mulish_700Bold,
              backgroundColor: theme.colors.coralRed,
            }}
          >
            <div>
              <span style={{marginLeft: '5px'}}>
                {cart.length > 0 ? payAmount.toLocaleString() : 0}
              </span>
              <span>تومان</span>
            </div>
          </div>
        </button>
      );
    }

    return null;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Optional: نمایش پیام موفقیت یا toast
  };
  
  const renderModal = (): JSX.Element => {
    return (
      <Drawer
        title="با ما تماس بگیرید"
        placement="right"
        closable={true}
        onClose={() => setModal(false)}
        open={modal}
        className="!max-w-[300px]"
        headerStyle={{ textAlign: 'right', fontFamily: 'inherit' }}
        bodyStyle={{ padding: '1rem' }}
        style={{zIndex: 1050 }}
      >
        <ul className="space-y-4 text-right font-medium">
          <li
            onClick={() => copyToClipboard('info@azimirang.com')}
            className="flex items-center cursor-pointer hover:text-blue-500"
          >
            <MailOutlined className="ml-2" />
            info@azimirang.com
          </li>
          <li
            onClick={() => copyToClipboard('01144235845 - 01144235846')}
            className="flex items-center cursor-pointer hover:text-blue-500"
          >
            <PhoneOutlined className="ml-2" />
            01144235845 - 01144235846
          </li>
          <li
            onClick={() => copyToClipboard('09008258000 - 09901059725')}
            className="flex items-center cursor-pointer hover:text-blue-500"
          >
            <MobileFilled className="ml-2" />
            09008258000 - 09901059725
          </li>
          <li
            onClick={() => copyToClipboard('01144235847')}
            className="flex items-center cursor-pointer hover:text-blue-500"
          >
            <MobileOutlined className="ml-2" />
            01144235847
          </li>
          <li
            onClick={() =>
              copyToClipboard(
                'مازندران‌ - آمل - کیلومتر یک کمربندی هراز به هزارسنگر - مجتمع رنگ عظیمی نیاکی'
              )
            }
            className="flex items-start cursor-pointer hover:text-blue-500"
          >
            <EnvironmentOutlined className="ml-2 mt-1" />
            <span className="whitespace-pre-line leading-6">
              مازندران‌ - آمل{"\n"}
              کیلومتر یک کمربندی هراز به هزارسنگر{"\n"}
              مجتمع رنگ عظیمی نیاکی
            </span>
          </li>
        </ul>
      </Drawer>
    );
  };

  const renderOverlay = (): JSX.Element => {
    return (
      <animated.div
        className='container'
        style={{
          width: '100%',
          height: '100vh',
          backgroundColor: 'rgba(25, 51, 100, 0.6)',
          position: 'fixed',
          inset: 0,
          zIndex: 500,
          overflow: 'hidden',
          ...backdropAnimation,
        }}
        onClick={() => setModal(false)}
      />
    );
  };
  const renderSearchInput = (): JSX.Element | null => {
    if (search) {
      return (
        <input
          type="text"
          placeholder="جستجو کنید..."
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            border: `1px solid ${theme.colors.aliceBlue2}`,
            maxWidth: 300,
            fontSize: 14,
            width: 'calc(100% - 100px)',
            textAlign: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            ...theme.fonts.Mulish_600SemiBold,
          }}
          onClick={(e) => {
            if (location.pathname !== "/shop") {
              navigate(`/shop`);
            }
          }}
        />
      );
    }
  
    return null;
  };
  
  return (
    <>
      {renderOverlay()}
      {renderModal()}
      <header
        style={{
          top: 0,
          height: 52,
          position: 'sticky',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row',
          backgroundColor: theme.colors.white,
          zIndex: 4,
          borderBottom: line ? `1px solid ${theme.colors.aliceBlue2}` : 'none',
        }}
      >
        {renderGoBack()}
        {!search && renderTitle()}
        {renderSearchInput()}
        {renderBasket()}
        {renderBurger()}
      </header>
    </>
  );
};
